import * as React from "react";
import * as S from "@styled";

import Select from "shared/components/select/Select";
import { Utilities } from "../../shared/utilities";
import { HttpBinaryResultParameterSchema, HttpResultParameterSchema } from "../model/HttpResultParameterSchema";
import { ElementType, IElement, IParameterSchema, ReturnType } from "../model/IProcessData";
import { ParameterEditorValue } from "../model/ParameterEditorValue";
import LogResultEditor from "./LogResultEditor";
import { default as ParameterInput } from "./ParameterInput";
import { ParameterList } from "./ParameterList";
import { default as PromoteResultEditor } from "./PromoteResultEditor";
import { SwitchCollapse } from "./SwitchCollapse";
import { Option } from "shared/model/Option";
import CustomToggle from "shared/components/CustomToggle";

export const ReturnEditor: React.FC<ReturnProps> = (props) => {
    const handleBypassGlobalExceptionHandlerChange = (checked: boolean) => {
        props.onChangeBypassGlobalExceptionHandler(
            {
                mode: "toggle",
                value: checked
            });
    };

    function handleChangeReturnType(event: Option) {
        const returnTypeValue = event.value;
        props.onChangeReturnType((ReturnType as { [key: string]: ReturnType })[returnTypeValue]);
    }

    const selectedReturnType = props.element.selectedTypeId || ReturnType.Expression;
    const isAdvancedPanelOpen = props.element.selectedTypeId && true || props.element.shouldNotLogResult;
    const parameterSchema: IParameterSchema = {
        inputType: "expression",
        type: "dynamic",
        name: "returnExpression",
        parameterType: "TextParameter",
        defaultValue: "",
        isSecret: false
    };

    const returnOptions: Option[] = [
        {
            label: FRENDS.Localizations.Expression,
            value: ReturnType.Expression
        }, {
            label: FRENDS.Localizations.HttpResponse,
            value: ReturnType.HttpResult
        }, {
            label: FRENDS.Localizations.HttpBinaryResponse,
            value: ReturnType.HttpBinaryResult
        }
    ]

    return (<div>
        <SwitchCollapse
            isOpenByDefault={isAdvancedPanelOpen}
            childrenCollapsed={null}
            childrenOpen={
                <div>
                    <S.FormGroup>
                        <Select
                            classNamePrefix="react-select"
                            id="resultTypeSelector"
                            value={Utilities.valueFromId(returnOptions, selectedReturnType)}
                            options={returnOptions}
                            onChange={handleChangeReturnType}
                            isDisabled={props.disabled}
                        />
                    </S.FormGroup>
                    <LogResultEditor elementId={props.id} />
                    <PromoteResultEditor id={props.element.id} />
                </div>
            }
        />
        <hr />
        {selectedReturnType === ReturnType.Expression
            ? <ParameterInput
                key="expression"
                name="expression"
                elementId={props.id}
                label="expression"
                value={props.element.parameters["expression"]}
                isValid={true}
                parameterSchema={parameterSchema}
            />
            : <ParameterList
                elementId={props.id}
                parentName=""
                argumentJson={((selectedReturnType === ReturnType.HttpBinaryResult)
                    ? HttpBinaryResultParameterSchema
                    : HttpResultParameterSchema)}
                existingParameters={props.element.parameters}
                onArrayItemAdd={props.onHttpHeaderArrayItemAdd}
                onArrayItemRemove={props.onHttpHeaderArrayItemRemove}
                onChangeArrayToExpression={props.onChangeHttpHeaderArrayToExpression}
                onArrayItemIndexChange={props.onHttpHeaderArrayItemIndexChange}
            />}
        {props.element.type === ElementType.Throw && props.hasGlobalExceptionHandler ?
            <S.FormGroup>
                <S.Label>
                    {FRENDS.Localizations.BypassGlobalExceptionHandler}
                </S.Label>
                <CustomToggle
                    id="return-bypass-global-exception-handler-selector"
                    // missing parameter signals older frends version, turn off for backwards compatibility
                    checked={props.element.parameters.bypassGlobalExceptionHandler?.value || false}
                    onChange={handleBypassGlobalExceptionHandlerChange}
                    disabled={props.disabled}
                />
            </S.FormGroup>
            :
            null
        }
    </div>
    );
};

interface ReturnProps {
    id: string;
    element: IElement;
    hasGlobalExceptionHandler: boolean;
    disabled?: boolean;
    onChangeReturnType: (newReturnType: ReturnType) => void;
    onHttpHeaderArrayItemAdd: (key: string, parameters: any) => void;
    onHttpHeaderArrayItemRemove: (key: string, index: number) => void;
    onHttpHeaderArrayItemIndexChange: (key: string, oldIndex: number, newIndex: number) => void;
    onChangeHttpHeaderArrayToExpression: (key: string) => void;
    onChangeBypassGlobalExceptionHandler: (value: ParameterEditorValue) => void;
}
